import { groupBy, set } from 'lodash'
import { filter } from 'rxjs'
import { computed, makeObservable, observable } from 'mobx'
import { tapAction, toStream } from 'utils/mobx-util'
import { isNotNil } from 'utils/object-util'
import { task } from 'mobx-task'
import Document, { DocumentField } from 'documents/Document'
import { DocumentActionsAdapter } from 'documents/DocumentActionsAdapter'
import { DocumentTypeField } from 'documents/DocumentType'

interface WorkpaperNode {
  type: DocumentTypeField
  field?: DocumentField
  position?: number
}

interface WorkpaperSchema {
  [key: string]: DocumentField | Array<WorkpaperSchema>
}

export default class WorkpapersFormViewModel {
  workpaperNodes: Array<string | WorkpaperNode> = []

  private readonly fieldsProcessed$ = toStream(
    () => this.document.extractionState,
    true
  ).pipe(filter((state) => isNotNil(state) && state !== 'Pending'))

  constructor(
    private document: Document,
    private adapter: DocumentActionsAdapter
  ) {
    makeObservable(this, {
      workpaperNodes: observable,
      isPending: computed,
      documentState: computed,
      documentTitle: computed,
      documentType: computed,
      isValid: computed,
    })

    this.fieldsProcessed$
      .pipe(
        tapAction(() => {
          // The list of `unordered` fields belonging to a workpaper, gets turned into
          // the workpaper schema so each field is properly located where it belongs
          // and then back to a list which would now be `ordered` by workpaper + key
          // prop with additional type information.
          //
          // This allows us to work with a flat list rather than infinite nested
          // components which can degrade performance as nesting gets deeper.
          this.workpaperNodes = mapDocumentFieldsToWorkpaperSchemas(
            document.fields
          ).flatMap((schema) => {
            const nodes = mapWorkpaperSchemasToWorkpaperNodes(
              schema,
              document.documentTypeFields
            )

            const node = nodes.find((node) => isNotNil(node.field))

            return node?.field ? [node.field.schemaId, ...nodes] : nodes
          })
        })
      )
      .subscribe()
  }

  get isPending() {
    return this.document.extractionState === 'Pending'
  }

  get documentState() {
    switch (this.document.extractionState) {
      case 'Extracted':
        return 'Review'
      case 'Validated':
        return 'Approved'
      default:
        return 'Pending'
    }
  }

  get documentTitle() {
    return this.document.title
  }

  get documentType() {
    return this.document.documentType?.name
  }

  get isValid() {
    if (!this.workpaperNodes.length) {
      return false
    }

    return this.workpaperNodes.every((node) =>
      typeof node === 'object' && node.field && node.type.required
        ? isNotNil(node.field.value) && node.field.value !== ''
        : true
    )
  }

  submit = task.resolved(() =>
    this.adapter.correctFields(
      this.document.id,
      this.document.latestVersion?.id,
      this.workpaperNodes
        .map((node) => (typeof node === 'object' ? node.field : null))
        .filter((field): field is DocumentField => isNotNil(field))
    )
  )

  activate() {
    // Nothing really but adding it for consistency
  }

  deactivate() {
    this.submit.reset()
  }
}

/*
 * Puts together the workpaper schema from a given list of fields.
 *
 * @example
 *
 * Given the following fields:
 *
 * [
 *   { id: 'ssn', path: '[0]', file_id: 'one', extracted_value: '1234' },
 *   { id: 'name', path: '[1]', file_id: 'one', extracted_value: 'Testy' }
 *   { id: 'ssn', path: '[1]', file_id: 'one', extracted_value: '9876' },
 *   { id: 'name', path: '[0]', file_id: 'one', extracted_value: 'Tester' }
 *   { id: 'ssn', path: '[0]', file_id: 'two', extracted_value: '5678' },
 *   { id: 'name', path: '[0]', file_id: 'two', extracted_value: 'Unkown' }
 * ]
 *
 * The output will be:
 *
 * [{
 *   ssn: { id: 'ssn', path: '[0]', file_id: 'one', extracted_value: '1234' },
 *   name: { id: 'name', path: '[0]', file_id: 'one', extracted_value: 'Tester' }
 * }, {
 *   ssn: { id: 'ssn', path: '[1]', file_id: 'one', extracted_value: '9876' },
 *   name: { id: 'name', path: '[1]', file_id: 'one', extracted_value: 'Testy' }
 * }, {
 *   ssn: { id: 'ssn', path: '[0]', file_id: 'two', extracted_value: '5678' },
 *   name: { id: 'name', path: '[0]', file_id: 'two', extracted_value: 'Unkown' }
 * }]
 */
function mapDocumentFieldsToWorkpaperSchemas(
  fields: Array<DocumentField>
): Array<WorkpaperSchema> {
  // We need to group by file_id first because there can be multiple workpapers found
  // per uploaded file and the path in which they appear is specific to each file_id.
  const fileWorkpapers = groupBy(fields, (field) => field.fileId)

  return Object.entries(fileWorkpapers).flatMap(([_, fileFields]) => {
    const workpapers: Array<WorkpaperSchema> = []

    fileFields.forEach((field) => {
      set(workpapers, `${field.path}.${field.id}`, field)
    })

    return workpapers
  })
}

/*
 * Decomposes a workpaper object into an `ordered` list of workpaper nodes.
 *
 * @example
 *
 * Given the following fields schemas
 *
 * [{
 *   ssn: { id: 'ssn', path: '[0]', file_id: 'one', extracted_value: '1234' },
 *   name: { id: 'name', path: '[0]', file_id: 'one', extracted_value: 'Tester' }
 * }, {
 *   ssn: { id: 'ssn', path: '[1]', file_id: 'one', extracted_value: '9876' },
 *   name: { id: 'name', path: '[1]', file_id: 'one', extracted_value: 'Testy' }
 * }, {
 *   ssn: { id: 'ssn', path: '[0]', file_id: 'two', extracted_value: '5678' },
 *   name: { id: 'name', path: '[0]', file_id: 'two', extracted_value: 'Unkown' }
 * }]
 *
 * The output will be:
 *
 * [{
 *   type,
 *   field: { id: 'ssn', path: '[0]', file_id: 'one', extracted_value: '1234' },
 * }, {
 *   type,
 *   field: { id: 'name', path: '[0]', file_id: 'one', extracted_value: 'Tester' }
 * }, {
 *   type,
 *   field: { id: 'ssn', path: '[1]', file_id: 'one', extracted_value: '9876' },
 * }, {
 *   type,
 *   field: { id: 'name', path: '[1]', file_id: 'one', extracted_value: 'Testy' }
 * }, {
 *   type,
 *   field: { id: 'ssn', path: '[0]', file_id: 'two', extracted_value: '5678' },
 * }, {
 *   type,
 *   field: { id: 'name', path: '[0]', file_id: 'two', extracted_value: 'Unkown' }
 * }]
 */
function mapWorkpaperSchemasToWorkpaperNodes(
  schema: WorkpaperSchema,
  documentTypeFields: Record<string, DocumentTypeField>
): Array<WorkpaperNode> {
  return Object.entries(schema)
    .sort(
      ([leftKey], [rightKey]) =>
        documentTypeFields[leftKey].sort_order -
        documentTypeFields[rightKey].sort_order
    )
    .flatMap(([key, value]) => {
      // No need to check for null as the fields have already been filtered by type
      // higher up.
      const fieldType = documentTypeFields[key]

      return Array.isArray(value)
        ? value.flatMap((nestedSchema, index) => [
            {
              type: fieldType,
              position: index + 1,
            },
            ...mapWorkpaperSchemasToWorkpaperNodes(
              nestedSchema,
              documentTypeFields
            ),
          ])
        : {
            type: fieldType,
            field: value,
          }
    })
}
